import React, { useContext } from 'react'
import {
  ArtboardDetailInfoFragment,
  useGetArtboardsForPageQuery,
} from '@sketch/gql-types'
import { useParams } from 'react-router-dom'
import { OperationVariables } from 'apollo-client'
import get from 'lodash.get'
import { RetryQueryProps, useQueryRetry } from 'utils'
import { ResponsiveValues } from '@sketch/global-styles'
import { ArtboardList } from 'modules/shares/components/ArtboardList'
import { ShareVersionContext } from 'modules/versioning'
import { RouteParams } from '@sketch/constants'

type RetryIfBuilder = (
  path: string[]
) => RetryQueryProps<any, OperationVariables>['retryIf']

const buildRetryIf: RetryIfBuilder = path => ({ data, error }) => {
  if (error) return true

  const artboards = get(data, path, []) as ArtboardDetailInfoFragment[]
  const files = artboards.map(a => a.files).flat()

  return files.some(file => !file?.url)
}

interface PageArtboardsProps {
  search: string
  columns: ResponsiveValues<number>
}

/**
 * Handles a list of artboards scoped to a specific share document
 * version page.
 */
export const PageArtboards = ({ search, columns }: PageArtboardsProps) => {
  const entriesPath = [
    'share',
    'version',
    'document',
    'page',
    'artboards',
    'entries',
  ]
  const afterPath = [
    'share',
    'version',
    'document',
    'page',
    'artboards',
    'meta',
    'after',
  ]

  const { versionShortId } = useContext(ShareVersionContext)!
  const { shareID, pageUUID } = useParams<RouteParams<'SHARE_PAGE_VIEW'>>()

  const { data, loading, error, fetchMore } = useQueryRetry(
    useGetArtboardsForPageQuery({
      variables: {
        identifier: shareID,
        versionShortId,
        search,
        pageUUID,
      },
    }),
    { retryIf: buildRetryIf(entriesPath) }
  )

  return (
    <>
      <ArtboardList
        fetchMore={fetchMore}
        afterPath={afterPath}
        entriesPath={entriesPath}
        pageUUID={pageUUID}
        data={data}
        loading={loading}
        error={error}
        columns={columns}
        filterValue={search}
      />
    </>
  )
}
